import { Chord } from "tonal";
import { pitchClass } from "./notes";

export type ChordQuality =
  | "maj" | "min" | "dim" | "aug" | "sus2" | "sus4"
  | "maj7" | "7" | "m7" | "m7b5" | "dim7" | "mMaj7"
  | "6" | "m6" | "add9" | "9" | "maj9" | "m9" | "7sus4";

/** Intervals in semitones above the root. */
export const CHORD_INTERVALS: Record<ChordQuality, readonly number[]> = {
  maj: [0, 4, 7],
  min: [0, 3, 7],
  dim: [0, 3, 6],
  aug: [0, 4, 8],
  sus2: [0, 2, 7],
  sus4: [0, 5, 7],
  maj7: [0, 4, 7, 11],
  "7": [0, 4, 7, 10],
  m7: [0, 3, 7, 10],
  m7b5: [0, 3, 6, 10],
  dim7: [0, 3, 6, 9],
  mMaj7: [0, 3, 7, 11],
  "6": [0, 4, 7, 9],
  m6: [0, 3, 7, 9],
  add9: [0, 4, 7, 14],
  "9": [0, 4, 7, 10, 14],
  maj9: [0, 4, 7, 11, 14],
  m9: [0, 3, 7, 10, 14],
  "7sus4": [0, 5, 7, 10],
};

export const CHORD_SUFFIX: Record<ChordQuality, string> = {
  maj: "", min: "m", dim: "dim", aug: "aug", sus2: "sus2", sus4: "sus4",
  maj7: "maj7", "7": "7", m7: "m7", m7b5: "m7b5", dim7: "dim7", mMaj7: "m(maj7)",
  "6": "6", m6: "m6", add9: "add9", "9": "9", maj9: "maj9", m9: "m9", "7sus4": "7sus4",
};

export const CHORD_DESCRIPTION: Record<ChordQuality, string> = {
  maj: "major triad", min: "minor triad", dim: "diminished triad", aug: "augmented triad",
  sus2: "suspended second", sus4: "suspended fourth", maj7: "major seventh chord",
  "7": "dominant seventh chord", m7: "minor seventh chord", m7b5: "half-diminished seventh chord",
  dim7: "diminished seventh chord", mMaj7: "minor-major seventh chord",
  "6": "major sixth chord", m6: "minor sixth chord", add9: "major add-nine chord", "9": "dominant ninth chord",
  maj9: "major ninth chord", m9: "minor ninth chord", "7sus4": "dominant seventh suspended-fourth chord",
};

/** Families for explorers and exercises. */
export const CHORD_GROUPS: { label: string; qualities: ChordQuality[] }[] = [
  { label: "Triads", qualities: ["maj", "min", "dim", "aug"] },
  { label: "Suspended", qualities: ["sus2", "sus4", "7sus4"] },
  { label: "Sevenths", qualities: ["maj7", "7", "m7", "m7b5", "dim7", "mMaj7"] },
  { label: "Sixths & extended", qualities: ["6", "m6", "add9", "9", "maj9", "m9"] },
];

/** MIDI notes of a chord in root position from a root MIDI note. */
export function buildChord(rootMidi: number, quality: ChordQuality): number[] {
  return CHORD_INTERVALS[quality].map((i) => rootMidi + i);
}

/**
 * Invert a voicing: inversion n moves the lowest note up an octave n times.
 * Inversion is taken modulo the number of notes.
 */
export function invert(notes: readonly number[], inversion: number): number[] {
  const out = [...notes].sort((a, b) => a - b);
  const n = out.length === 0 ? 0 : ((inversion % out.length) + out.length) % out.length;
  for (let i = 0; i < n; i++) out.push(out.shift()! + 12);
  return out;
}

export interface DetectedChord {
  rootPc: number;
  quality: ChordQuality;
  /** 0 = root position, 1 = third in bass, … ; -1 when bass isn't a chord tone. */
  inversion: number;
  bassPc: number;
}

// Earlier entries win ties between equally plausible readings.
const DETECTION_ORDER: ChordQuality[] = [
  "maj", "min", "dim", "aug", "7", "maj7", "m7", "m7b5", "dim7", "mMaj7", "sus4", "sus2",
  "6", "m6", "add9", "7sus4", "9", "maj9", "m9",
];

/**
 * Detect a chord from MIDI notes by matching pitch-class sets exactly.
 * Prefers interpretations whose root is the bass (root position).
 * Returns null when no exact match exists (e.g. two notes, clusters).
 */
export function detectChord(midiNotes: readonly number[]): DetectedChord | null {
  if (midiNotes.length < 3) return null;
  const sorted = [...midiNotes].sort((a, b) => a - b);
  const bassPc = pitchClass(sorted[0]!);
  const pcs = [...new Set(sorted.map(pitchClass))];
  const candidates: DetectedChord[] = [];
  for (const rootPc of pcs) {
    const rel = new Set(pcs.map((pc) => (pc - rootPc + 12) % 12));
    for (const quality of DETECTION_ORDER) {
      const iv = CHORD_INTERVALS[quality].map((i) => i % 12); // ninths compare as pitch classes
      if (iv.length !== rel.size || !iv.every((i) => rel.has(i))) continue;
      const bassInterval = (bassPc - rootPc + 12) % 12;
      candidates.push({ rootPc, quality, bassPc, inversion: iv.indexOf(bassInterval) });
    }
  }
  if (candidates.length === 0) return null;
  // Symmetric chords (aug, dim7) match several roots: prefer the bass as root.
  candidates.sort((a, b) => a.inversion - b.inversion || DETECTION_ORDER.indexOf(a.quality) - DETECTION_ORDER.indexOf(b.quality));
  return candidates[0]!;
}

export function inversionName(inversion: number): string {
  return ["root position", "first inversion", "second inversion", "third inversion"][inversion] ?? "inverted";
}

/** tonal's chord-type symbols, used only for enharmonically correct spelling. */
const TONAL_SYMBOL: Record<ChordQuality, string> = {
  maj: "M", min: "m", dim: "dim", aug: "aug", sus2: "sus2", sus4: "sus4", maj7: "maj7", "7": "7", m7: "m7",
  m7b5: "m7b5", dim7: "dim7", mMaj7: "mMaj7", "6": "6", m6: "m6", add9: "Madd9", "9": "9", maj9: "maj9", m9: "m9", "7sus4": "7sus4",
};

/** Spelled chord tones in root-position order (e.g. Bb m7 → Bb Db F Ab; C dim7 → C Eb Gb Bbb). */
export function spellChord(root: string, quality: ChordQuality): string[] {
  const c = Chord.get(root + TONAL_SYMBOL[quality]);
  if (c.empty) throw new Error(`Cannot spell chord ${root} ${quality}`);
  // tonal lists add9 as C E G D; keep our interval order.
  return quality === "add9" ? [c.notes[0]!, c.notes[1]!, c.notes[2]!, c.notes[3]!] : c.notes;
}
